/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Page de detail d'un service du site UK.
 *
 * Le service est retrouve par son `slug` dans les traductions. Un slug inconnu
 * — lien obsolete, adresse saisie a la main — ne doit pas produire une page
 * vide : la page bascule alors vers la liste des services.
 */
import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";
import { SERVICE_IMAGES } from "../lib/media";
import CalendlyButton from "../components/CalendlyButton";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";

const ServiceDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  if (!t) return null;

  const service = t.services.items.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const heroImg = SERVICE_IMAGES[service.slug];

  return (
    <main data-testid={`service-detail-${slug}`} className="pt-20">
      <SEO
        title={`${service.title} — Prenim`}
        description={service.short}
        lang={lang}
      />

      {/* Hero */}
      <section className="relative bg-slate-950 text-white py-24 lg:py-32 overflow-hidden">
        {heroImg && (
          <div
            className="absolute inset-0 opacity-25 bg-cover bg-center"
            style={{ backgroundImage: `url('${heroImg}')` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 to-slate-950/60" />
        <div className="relative max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/services"
            data-testid="back-to-services"
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-cyan-400 mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 rtl-flip" />
            {t.common.back_to_services}
          </Link>

          <div className="flex items-center gap-5 mb-6">
            <span className="relative inline-block h-20 w-20 rounded-full overflow-hidden ring-2 ring-cyan-400/60 shadow-lg shadow-cyan-400/20 shrink-0">
              {heroImg && (
                <img
                  src={heroImg}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                />
              )}
              <span className="absolute inset-0 bg-gradient-to-tr from-blue-600/30 to-transparent" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400">
              {t.navigation.services}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl">
            {service.title}
          </h1>
          <p className="mt-6 text-lg lg:text-xl text-slate-300 max-w-2xl">
            {service.page.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xl lg:text-2xl text-slate-700 leading-relaxed">{service.page.intro}</p>
        </div>
      </section>

      {/* Benefits */}
      <section data-testid="service-benefits" className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-950 mb-12">
            {lang === "fr" ? "Bénéfices" : lang === "es" ? "Beneficios" : lang === "ar" ? "الفوائد" : "Benefits"}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {service.page.benefits.map((b, i) => (
              <div
                key={i}
                data-testid={`service-benefit-${i}`}
                className="bg-white rounded-2xl p-7 border border-slate-200 card-lift"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-600 mb-5">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-slate-950 mb-2">{b.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section data-testid="service-process" className="py-20 lg:py-24">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-950 mb-12">
            {lang === "fr" ? "Notre démarche" : lang === "es" ? "Nuestro proceso" : lang === "ar" ? "منهجيتنا" : "Our process"}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {service.page.process.map((p, i) => (
              <div key={i} data-testid={`service-process-${i}`} className="relative">
                <div className="text-6xl font-bold text-slate-100 absolute -top-2 -start-1 select-none">
                  0{i + 1}
                </div>
                <div className="relative pt-6">
                  <h3 className="text-xl font-semibold text-slate-950 mb-2">{p.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section data-testid="service-usecases" className="py-20 lg:py-24 bg-slate-950 text-white">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-12">
            {lang === "fr" ? "Cas d'usage" : lang === "es" ? "Casos de uso" : lang === "ar" ? "حالات الاستخدام" : "Use cases"}
          </h2>
          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {service.page.use_cases.map((uc, i) => (
              <li
                key={i}
                data-testid={`service-usecase-${i}`}
                className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-5 hover:border-cyan-400/50 transition-colors"
              >
                <CheckCircle2 className="h-5 w-5 text-cyan-400 mt-0.5 shrink-0" />
                <span className="text-slate-100 font-medium">{uc}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold">{t.home.cta_band.title}</h2>
          <p className="mt-4 text-blue-100 text-lg">{t.home.cta_band.subtitle}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <CalendlyButton
              variant="primary"
              testId={`service-${slug}-calendly`}
            >
              {t.common.book_consultation}
            </CalendlyButton>
            <Link
              to="/contact"
              data-testid="service-bottom-cta"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 hover:bg-white text-white hover:text-blue-700 font-semibold px-6 py-3 transition-colors"
            >
              {t.home.cta_band.cta}
              <ArrowRight className="h-4 w-4 rtl-flip" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServiceDetail;
